// Iter75 — Sitewide safe image with automatic fallback.
//   • Tries the provided src first.
//   • If the URL is relative (starts with /api/static/... or /uploads/...),
//     prefixes it with REACT_APP_BACKEND_URL so it resolves on production.
//   • On image-load failure, swaps to an inline data-URI Lifted At The
//     Moment placeholder SVG. The placeholder is embedded — it can NEVER
//     fail to load (no DNS, no CDN, no CORS), which is the only way to
//     guarantee zero browser broken-image icons site-wide.
//   • Eliminates the browser "broken image" icon globally.
//
// Drop-in replacement for <img>. Same props.
import React, { useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Inline SVG placeholder. Uses the brand green (#84cc16-ish) on a dark
// background with the Lifted "L" mark. data: URIs are loaded synchronously
// from the document, so they cannot 404, CORS-fail, or DNS-fail.
export const LATM_FALLBACK_LOGO =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'>
      <rect width='200' height='200' fill='#0c1a0c'/>
      <rect x='6' y='6' width='188' height='188' fill='none' stroke='#1f2e1f' stroke-width='2' rx='12'/>
      <text x='100' y='92' font-family='Anton, Impact, sans-serif' font-size='44'
            text-anchor='middle' fill='#a3e635' letter-spacing='4'>LATM</text>
      <text x='100' y='128' font-family='Bebas Neue, Arial, sans-serif' font-size='14'
            text-anchor='middle' fill='#6b7c5e' letter-spacing='3'>LIFTED AT THE MOMENT</text>
    </svg>`,
  );

export function resolveImageUrl(src) {
  if (!src || typeof src !== "string") return LATM_FALLBACK_LOGO;
  const s = src.trim();
  if (!s) return LATM_FALLBACK_LOGO;
  if (s.startsWith("data:") || s.startsWith("blob:")) return s;
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/")) return API ? `${API}${s}` : s;
  return s;
}

export default function SafeImage({
  src,
  alt = "",
  fallback = LATM_FALLBACK_LOGO,
  className = "",
  loading = "lazy",
  onError,
  ...rest
}) {
  const initial = resolveImageUrl(src);
  const [current, setCurrent] = useState(initial);
  const [failed, setFailed] = useState(false);
  // Reset when src changes
  React.useEffect(() => {
    setCurrent(resolveImageUrl(src));
    setFailed(false);
  }, [src]);
  const handleErr = (e) => {
    if (!failed) {
      if (process.env.NODE_ENV !== "production") {
        // Only noisy in dev — keep prod consoles clean
        console.warn("[SafeImage] image failed, using LATM fallback:", current);
      }
      setFailed(true);
      setCurrent(fallback);
    }
    if (onError) onError(e);
  };
  return (
    <img
      src={current}
      alt={alt}
      loading={loading}
      onError={handleErr}
      className={className}
      {...rest}
    />
  );
}
